import React, { useEffect, useMemo } from 'react';
import { withParentSize } from '@visx/responsive';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useUserContext from 'Context/UserContext';
import { pickStyles } from 'Helpers/Affordances';
import classnames from 'classnames';
import fr from 'facet-rest';
import useCustomLayout from './useCustomLayout';
import Column from './Column';

const styles = theme => ({
  root: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  column: {
    marginTop: -1, // Counter the size of the border
    borderTop: `1px solid ${theme.palette.facet.mediumGray}`,
  },
  newColumn: {
    marginTop: -1, // Counter the size of the border
    borderTop: `1px solid ${theme.palette.facet.mediumGray}`,
  },
  columnLeftBorder: {
    borderLeft: `1px solid ${theme.palette.facet.mediumGray}`,
  },
});

const CustomLayout = withParentSize(
  ({
    className,
    classes,
    contentKey,
    layoutLink,
    parentWidth,
    parentHeight,
    tabRegistry,
    ...rest
  }) => {
    const {
      rootNodeProps,
      closeTab,
      dragTab,
      layoutResource,
      moveTabToColumn,
      moveTabToNewColumn,
      openTab,
      openTabNames,
      setActiveTab,
      resizeColumnSave,
      popOutTab,
      openTabs,
    } = useCustomLayout(layoutLink);
    const { agentResource } = useUserContext();
    const columnResources = layoutResource?.attr('columns').resources().list();

    const onDragEnd = (result) => {
      if (!(result && result.destination)) {
        return;
      }

      const {
        draggableId: tabName,
        destination,
        source,
      } = result;

      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        // No-op, it was dropped back where it started.
        return;
      }

      const {
        droppableId,
        index: tabIdx,
      } = destination;

      // Droppable ID is like: "columnIndex.1"
      const columnIndex = Number(droppableId.split('.')[1]);

      dragTab(tabName, columnIndex, tabIdx);
    };

    const hasAtLeastOneValidTabInAnyColumn = useMemo(() => {
      const columns = layoutResource?.attr('columns').resources().list();
      if (!columns) return null;
      for (const col of columns) {
        for (const tab of col.attr('tabs').resources().list()) {
          if (tabRegistry[tab.attr('name')]) return true;
        }
      }
      return false;
    }, [layoutResource, tabRegistry]);

    useEffect(() => {
      const columns = layoutResource?.attr('columns').resources().list();
      if (!columns) return;
      columns.forEach((col) => {
        col.attr('tabs').resources().list().forEach((el) => {
          const name = el.attr('name');
          if (!tabRegistry[name] && hasAtLeastOneValidTabInAnyColumn) {
            closeTab(name);
          }
        });
      });
    }, [layoutResource, hasAtLeastOneValidTabInAnyColumn, tabRegistry]);

    if (!layoutResource) return null;
    return (
      <div
        className={classnames(classes.root, className)}
        {...rest}
        {...rootNodeProps}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          {columnResources.map((_, i) => {
            const columnStyles = pickStyles(agentResource, {
              [fr.relationships.default.name]: [classes.newColumn, {
                [classes.columnLeftBorder]: i > 0,
              }],
            });
            return (
              <Column
                className={columnStyles}
                key={i} // eslint-disable-line react/no-array-index-key
                layoutResource={layoutResource}
                columnIndex={i}
                layoutHeight={parentHeight}
                layoutWidth={parentWidth}
                tabRegistry={tabRegistry}
                resizeColumnSave={resizeColumnSave}
                TabsProps={{
                  closeTab,
                  layoutResource,
                  moveTabToColumn,
                  moveTabToNewColumn,
                  openTab,
                  setActiveTab,
                  openTabNames,
                  popOutTab,
                  openTabs,
                }}
                ContentProps={{
                  key: contentKey,
                }}
              />
            );
          })}
        </DragDropContext>
      </div>
    );
  },
);

CustomLayout.defaultProps = {
  className: '',
  contentKey: undefined,
};

CustomLayout.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  layoutLink: PropTypes.object.isRequired,
  tabRegistry: PropTypes.object.isRequired,
  contentKey: PropTypes.string,
};

export default withStyles(styles)(CustomLayout);
